import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiConfiguration } from '../api/api-configuration';
import {
  authenticate$Json,
  Authenticate$Json$Params
} from '../api/fn/auth/authenticate-json';
import { AuthenticateRequest } from '../api/models/authenticate-request';
import { AuthenticateResponse } from '../api/models/authenticate-response';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private tokenSubject: BehaviorSubject<string | null>;
  public token$: Observable<string | null>;

  constructor(
    private http: HttpClient,
    private apiConfig: ApiConfiguration
  ) {
    const storedToken = localStorage.getItem('jwt_token');
    this.tokenSubject = new BehaviorSubject<string | null>(storedToken);
    this.token$ = this.tokenSubject.asObservable();
  }

  public get tokenValue(): string | null {
    return this.tokenSubject.value;
  }

  authenticate(user1Id: string, user2Id: string, validationKey: string): Observable<AuthenticateResponse> {
    const request: AuthenticateRequest = {
      user1Id,
      user2Id,
      validationKey
    };

    const params: Authenticate$Json$Params = {
      body: request
    };

    return authenticate$Json(
      this.http,
      this.apiConfig.rootUrl,
      params
    ).pipe(
      map(response => {
        const authResponse = response.body as AuthenticateResponse;
        if (authResponse.success && authResponse.token) {
          localStorage.setItem('jwt_token', authResponse.token);
          if (authResponse.expiresAt) {
            localStorage.setItem('jwt_expires', authResponse.expiresAt);
          }
          this.tokenSubject.next(authResponse.token);
        }
        return authResponse;
      })
    );
  }

  logout(): void {
    localStorage.removeItem('jwt_token');
    localStorage.removeItem('jwt_expires');
    this.tokenSubject.next(null);
  }

  isAuthenticated(): boolean {
    const token = this.tokenValue;
    if (!token) {
      return false;
    }

    const expiresAt = localStorage.getItem('jwt_expires');
    if (expiresAt) {
      const expirationDate = new Date(expiresAt);
      return expirationDate > new Date();
    }

    return true;
  }

  getTokenClaims(): { [key: string]: string } | null {
    const token = this.tokenValue;
    if (!token) {
      return null;
    }

    try {
      const payload = token.split('.')[1];
      const decoded = atob(payload);
      return JSON.parse(decoded);
    } catch {
      return null;
    }
  }

  getUser1Email(): string {
    const claims = this.getTokenClaims();
    if (claims && claims['user1Email']) {
      return claims['user1Email'];
    }
    return "";
  }

  getUser1FirstName(): string {
    const claims = this.getTokenClaims();
    if (claims && claims['user1FirstName']) {
      return claims['user1FirstName'];
    }
    return "";
  }

  getUser1LastName(): string {
    const claims = this.getTokenClaims();
    if (claims && claims['user1LastName']) {
      return claims['user1LastName'];
    }
    return "";
  }

  getUser2Email(): string {
    const claims = this.getTokenClaims();
    if (claims && claims['user2Email']) {
      return claims['user2Email'];
    }
    return "";
  }

  getUser2FirstName(): string {
    const claims = this.getTokenClaims();
    if (claims && claims['user2FirstName']) {
      return claims['user2FirstName'];
    }
    return "";
  }

  getUser2LastName(): string {
    const claims = this.getTokenClaims();
    if (claims && claims['user2LastName']) {
      return claims['user2LastName'];
    }
    return "";
  }

  getMeetingMinutes(): number {
    const claims = this.getTokenClaims();
    if (claims && claims['meetingMinutes']) {
      return parseInt(claims['meetingMinutes'], 10);
    }
    return 60; // Default to 60 minutes
  }

  getMeetingTypeName(): string {
    const claims = this.getTokenClaims();
    if (claims && claims['meetingTypeName']) {
      return claims['meetingTypeName'];
    }
    return 'Meeting';
  }

  getMeetingLocationName(): string {
    const claims = this.getTokenClaims();
    if (claims && claims['meetingLocationName']) {
      return claims['meetingLocationName'];
    }
    return 'Location';
  }

  getCaseId(): number {
    const claims = this.getTokenClaims();
    if (claims && claims['caseId']) {
      return parseInt(claims['caseId'], 10);
    }
    return 0;
  }

  getSentDate(): Date | null {
    const claims = this.getTokenClaims();
    if (claims && claims['sentDate']) {
      return new Date(claims['sentDate']);
    }
    return null;
  }
}
